import React from 'react';
import GhostAnalysisPanel from './GhostAnalysisPanel';
import TierMatrix from './TierMatrix';
import type {
  DynamicResultData,
  AggregatePerformanceBucket,
  MetaAnalysisLite,
} from './types';
import { SEVERITY_RANK, twoSidedSignificance } from './format';

interface DynamicAnalysisResultProps {
  resultData: DynamicResultData;
  meta?: MetaAnalysisLite | null;
}

const tierNumber = (bucket?: string): number => {
  const m = bucket?.match(/Tier(\d+)$/);
  return m ? parseInt(m[1], 10) : 0;
};

/** One-line bridge relating the acute ghost signal to the recent-window trend. */
const bridgeLine = (
  ghostSeverityRank: number,
  tiers: AggregatePerformanceBucket[],
): string => {
  const maxTier = Math.max(...tiers.map((t) => tierNumber(t.bucket)));
  const nonBaseline = tiers.filter((t) => tierNumber(t.bucket) !== maxTier);

  const tierSeverity = (t: AggregatePerformanceBucket): number => {
    const sev = t.interArrivalTimeMeanStatisticalSignificanceTest ?? twoSidedSignificance(t.interArrivalTimeMeanZScore);
    return sev ? SEVERITY_RANK[sev] : -1;
  };
  const allRecentNormal = nonBaseline.every((t) => tierSeverity(t) <= 0);

  if (ghostSeverityRank >= 1 && allRecentNormal) {
    return 'The silence is new — every recent window up to the last sale still reads Normal below. Tiers measure the window up to the last beat; the ghost measures the silence since.';
  }
  return 'The ghost reads the silence right now; the tiers below read the trend up to the last beat — read them together.';
};

const DynamicAnalysisResult: React.FC<DynamicAnalysisResultProps> = ({ resultData, meta }) => {
  // Failed / error result — the pages gate on significance, so render nothing.
  if (resultData.error || (!resultData.ghostTransactionAnalysis && !resultData.aggregatePerformanceAnalysis)) {
    return null;
  }

  const ghost = resultData.ghostTransactionAnalysis;
  const tiers = resultData.aggregatePerformanceAnalysis ?? [];
  const hasTiers = tiers.length > 0;

  const ghostSeverityRank = ghost?.absenceSeverityLevel
    ? SEVERITY_RANK[ghost.absenceSeverityLevel]
    : 0;

  return (
    <div className="space-y-5">
      {ghost && <GhostAnalysisPanel ghost={ghost} meta={meta} />}

      {hasTiers && (
        <>
          <div className="rounded-lg border border-primary/30 bg-accent px-3 py-2.5 text-[11.5px] leading-relaxed text-accent-foreground">
            {bridgeLine(ghostSeverityRank, tiers)}
          </div>
          <TierMatrix tiers={tiers} />
        </>
      )}
    </div>
  );
};

export default DynamicAnalysisResult;
